import { useState, useRef, useEffect } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Sparkles, X, Send, Loader2, Bot, User } from "lucide-react";

/**
 * AI Stok Asistanı — floating chat bubble that queries /api/ai/inventory-chat.
 * The backend already scopes all data to the caller's tenant, so nothing here
 * needs tenant handling.
 */
export default function InventoryAssistant() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    { role: "assistant", text: "Merhaba! Ben Mağaza Asistanınım 💎 Dükkanınızın verilerini bana sorabilirsiniz: stok, kar, ayar dağılımı, en kârlı gün — her şey burada." },
  ]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const boxRef = useRef(null);

  useEffect(() => {
    if (boxRef.current) boxRef.current.scrollTop = boxRef.current.scrollHeight;
  }, [messages, open]);

  const send = async () => {
    const q = input.trim();
    if (!q || sending) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", text: q }]);
    setSending(true);
    try {
      const r = await api.post("/ai/inventory-chat", { message: q, session_id: sessionId });
      setSessionId(r.data.session_id);
      setMessages((m) => [...m, { role: "assistant", text: r.data.answer || "…" }]);
    } catch (e) {
      const detail = e?.response?.data?.detail || "Yanıt alınamadı";
      toast.error(detail);
      setMessages((m) => [...m, { role: "assistant", text: "❌ " + detail, error: true }]);
    } finally { setSending(false); }
  };

  const quickPrompts = [
    "Şu an elimde toplam kaç gram altın var?",
    "Bu ayın en kârlı günü hangisi?",
    "Stokta 5 adetten az kalan ürünleri listele",
    "Ayar bazında dağılımım nasıl?",
  ];

  return (
    <>
      {/* Floating trigger button */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          className="fixed bottom-24 right-6 z-40 h-14 w-14 rounded-full flex items-center justify-center shadow-2xl transition-transform hover:scale-105"
          style={{ background: "linear-gradient(135deg, var(--gold) 0%, #c2a24a 100%)", color: "#0a0a0a" }}
          data-testid="inventory-assistant-open"
          aria-label="Mağaza Asistanı"
        >
          <Sparkles size={22} />
          <span className="absolute -top-1 -right-1 h-3 w-3 rounded-full bg-emerald-400 animate-pulse ring-2 ring-black" />
        </button>
      )}

      {/* Chat panel */}
      {open && (
        <div className="fixed bottom-24 right-3 sm:right-6 z-40 w-[calc(100vw-1.5rem)] sm:w-[92vw] max-w-md border border-white/10 bg-[#080808] shadow-2xl flex flex-col" style={{ height: "min(70vh, 560px)" }} data-testid="inventory-assistant-panel">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-white/10" style={{ background: "linear-gradient(135deg, rgba(212,175,55,0.14) 0%, rgba(30,100,255,0.05) 100%)" }}>
            <div className="flex items-center gap-2.5">
              <div className="h-8 w-8 rounded-full flex items-center justify-center" style={{ background: "var(--gold)", color: "#0a0a0a" }}>
                <Sparkles size={14} />
              </div>
              <div>
                <div className="font-display text-sm">Mağaza Asistanı</div>
                <div className="text-[10px] text-neutral-500 font-mono tracking-wider">AI · gemini 3.1 pro · sadece senin verinle</div>
              </div>
            </div>
            <button onClick={() => setOpen(false)} className="text-neutral-500 hover:text-white" data-testid="inventory-assistant-close">
              <X size={18} />
            </button>
          </div>

          {/* Messages */}
          <div ref={boxRef} className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="assistant-messages">
            {messages.map((m, i) => (
              <div key={i} className={`flex items-start gap-2 ${m.role === "user" ? "flex-row-reverse" : ""}`}>
                <div className={`h-7 w-7 rounded-full flex items-center justify-center shrink-0 ${m.role === "user" ? "bg-neutral-800 text-neutral-300" : ""}`}
                     style={m.role === "assistant" ? { background: "var(--gold)", color: "#0a0a0a" } : {}}>
                  {m.role === "user" ? <User size={13} /> : <Bot size={13} />}
                </div>
                <div className={`text-sm leading-relaxed whitespace-pre-wrap max-w-[85%] px-3 py-2 ${
                  m.role === "user" ? "bg-neutral-800/60 text-white" : (m.error ? "bg-red-500/10 text-red-300 border border-red-500/30" : "bg-white/[.03] text-neutral-100 border border-white/8")
                }`}>{m.text}</div>
              </div>
            ))}
            {sending && (
              <div className="flex items-center gap-2 text-xs text-neutral-500">
                <Loader2 size={12} className="animate-spin" /> Asistan düşünüyor…
              </div>
            )}
          </div>

          {/* Quick prompts */}
          {messages.length < 3 && (
            <div className="px-4 pb-2 flex flex-wrap gap-1.5">
              {quickPrompts.map((q, i) => (
                <button key={i} onClick={() => setInput(q)} className="text-[11px] px-2.5 py-1 border border-white/10 text-neutral-400 hover:border-[--gold] hover:text-[--gold]" data-testid={`quick-prompt-${i}`}>
                  {q}
                </button>
              ))}
            </div>
          )}

          {/* Input */}
          <div className="p-3 border-t border-white/10 flex items-center gap-2">
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && send()}
              placeholder="Stokla ilgili bir soru sor…"
              className="flex-1 bg-transparent border border-white/10 px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-[--gold] placeholder-neutral-600"
              disabled={sending}
              data-testid="assistant-input"
            />
            <button
              onClick={send}
              disabled={sending || !input.trim()}
              className="btn-primary-gold px-4 py-2.5 flex items-center gap-1 disabled:opacity-40"
              data-testid="assistant-send"
            >
              {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
            </button>
          </div>
        </div>
      )}
    </>
  );
}
